import { useRef } from 'react';
import { useBoolean } from 'usehooks-ts';
import { Flex, Icon, Input, Text, cn } from '@kognitos/lattice';

interface IFileDropzoneProps {
  /** Unique id for the hidden input; several dropzones can be mounted at once. */
  inputId: string;
  /** Translated prompt, also the zone's accessible name. */
  label: string;
  onFiles: (files: FileList | null) => void;
  /** Translated hint under the zone, e.g. the accepted file types. */
  caption?: string;
  /** `accept` for the picker. Drops bypass it, so callers still validate. */
  accept?: string;
  multiple?: boolean;
  /** Overrides the default 160px height. */
  className?: string;
}

/**
 * Click-or-drop upload area. Owns its own picker and drag state so callers only
 * handle the files that come out of it.
 */
export function FileDropzone({
  inputId,
  label,
  onFiles,
  caption,
  accept,
  multiple = false,
  className,
}: IFileDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    value: isDragging,
    setTrue: setDragging,
    setFalse: clearDragging,
  } = useBoolean();

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap={2}
        width="full"
        padding={2}
        onClick={handleBrowseClick}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBrowseClick();
          }
        }}
        onDrop={event => {
          event.preventDefault();
          clearDragging();
          onFiles(event.dataTransfer.files);
        }}
        onDragOver={event => {
          event.preventDefault();
          setDragging();
        }}
        onDragLeave={clearDragging}
        role="button"
        tabIndex={0}
        aria-label={label}
        className={cn(
          'h-[160px] cursor-pointer rounded-md border border-dashed transition-colors',
          isDragging
            ? 'border-primary bg-primary/10'
            : 'border-border bg-muted hover:border-primary/50',
          className,
        )}
      >
        <Flex align="center" gap={2}>
          <Icon type="Upload" size="sm" />
          <Text>{label}</Text>
        </Flex>
      </Flex>
      <Input
        ref={fileInputRef}
        id={inputId}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={event => {
          onFiles(event.target.files);
          // Let the same file be picked again after it is removed.
          event.target.value = '';
        }}
      />
      {caption && (
        <Text color="muted" level="small">
          {caption}
        </Text>
      )}
    </>
  );
}

export default FileDropzone;
